"use client";

import { useState } from "react";
import { useRouter } from "next/navigation";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { 
  Users, 
  ShieldCheck, 
  UserPlus, 
  Search, 
  Lock, 
  Code, 
  Send, 
  ArrowRightLeft, 
  ArrowRight,
  Wallet, 
  Building2, 
  CheckCircle2, 
  Trash2, 
  Mail, 
  ShieldAlert, 
  Plus, 
  Info, 
  MoreVertical,
  Check,
  Eye,
  Edit2,
  Clock,
  Sparkles,
  Layers
} from "lucide-react";
import { cn } from "@/lib/utils";

// Definition of Permission Groups
interface PermissionItem {
  id: string;
  name: string;
  description: string;
}

interface PermissionGroup {
  category: string;
  icon: any;
  permissions: PermissionItem[];
}

const ALL_PERMISSION_GROUPS: PermissionGroup[] = [
  {
    category: "User & Team Management",
    icon: Users,
    permissions: [
      { id: "users.view", name: "View Team Members", description: "View the organization member roster and roles." },
      { id: "users.invite", name: "Invite Team Members", description: "Send email invites to onboard new staff." },
      { id: "users.change_role", name: "Modify User Roles", description: "Assign or modify assigned roles of staff members." },
      { id: "users.remove", name: "Remove Members", description: "Revoke dashboard and API access for staff." }
    ]
  },
  {
    category: "Role Setup & Governance",
    icon: ShieldCheck,
    permissions: [
      { id: "roles.view", name: "View Roles & Permissions", description: "Access the role matrix and permission definitions." },
      { id: "roles.create", name: "Create Custom Roles", description: "Build new organization roles with custom privileges." },
      { id: "roles.edit", name: "Edit Custom Roles", description: "Update descriptions and toggled permissions of custom roles." },
      { id: "roles.delete", name: "Delete Custom Roles", description: "Delete non-system custom roles." }
    ]
  },
  {
    category: "Payouts & Disbursements",
    icon: Send,
    permissions: [
      { id: "payouts.view", name: "View Payout History", description: "View single and bulk disbursement records." },
      { id: "payouts.initiate", name: "Initiate Payouts", description: "Create single and bulk transfer requests." },
      { id: "payouts.approve", name: "Authorize High-Value Payouts", description: "Dual-control authorization for transfers exceeding approval threshold." },
      { id: "payouts.beneficiaries", name: "Manage Beneficiaries", description: "Add, edit, and whitelist recipient bank accounts." }
    ]
  },
  {
    category: "Transactions & Invoicing",
    icon: ArrowRightLeft,
    permissions: [
      { id: "transactions.view", name: "View Transactions", description: "Inspect payment logs, fees, and customer payments." },
      { id: "transactions.refund", name: "Process Refunds", description: "Authorize and issue full or partial charge refunds." },
      { id: "transactions.export", name: "Export Statements", description: "Download CSV and Excel reconciliation statements." },
      { id: "invoicing.manage", name: "Manage Invoices", description: "Create, dispatch, and track customer digital invoices." }
    ]
  },
  {
    category: "Balances & Virtual Accounts",
    icon: Wallet,
    permissions: [
      { id: "balances.view", name: "View Balances & Ledger", description: "Inspect ledger balances, pending settlements, and reserves." },
      { id: "balances.fund", name: "Fund Wallet", description: "Initiate wallet top-ups via bank transfer." },
      { id: "virtual_accounts.view", name: "View Virtual Accounts", description: "Inspect dedicated and dynamic customer NUBAN accounts." },
      { id: "virtual_accounts.create", name: "Issue Virtual Accounts", description: "Generate new dedicated accounts for customers." }
    ]
  },
  {
    category: "Developer & API Settings",
    icon: Code,
    permissions: [
      { id: "developer.keys_view", name: "View API Keys", description: "Reveal public and secret API credentials." },
      { id: "developer.keys_rotate", name: "Rotate Secret Keys", description: "Roll and invalidate API secrets." },
      { id: "developer.webhooks", name: "Configure Webhooks", description: "Register endpoint URLs and inspect delivery logs." }
    ]
  },
  {
    category: "Business & Compliance Settings",
    icon: Building2,
    permissions: [
      { id: "settings.view", name: "View Corporate Profile", description: "View registered CAC information and address." },
      { id: "settings.channels", name: "Toggle Payment Channels", description: "Enable or disable card, USSD, and transfer methods." },
      { id: "compliance.manage", name: "Manage KYC Documents", description: "Submit board resolutions, CAC, and director IDs." }
    ]
  }
];

// Initial Roles
interface Role {
  id: string;
  name: string;
  description: string;
  isSystem: boolean;
  memberCount: number;
  permissions: string[];
}

const INITIAL_ROLES: Role[] = [
  {
    id: "role-super-admin",
    name: "Super Administrator",
    description: "Unrestricted master access to all operations, payouts, developer keys, and governance.",
    isSystem: true,
    memberCount: 1,
    permissions: ALL_PERMISSION_GROUPS.flatMap(g => g.permissions.map(p => p.id))
  },
  {
    id: "role-developer",
    name: "Developer & Integration Lead",
    description: "Access to API credentials, webhooks, sandbox simulation, and technical documentation.",
    isSystem: true,
    memberCount: 1,
    permissions: [
      "developer.keys_view", 
      "developer.keys_rotate", 
      "developer.webhooks", 
      "transactions.view", 
      "virtual_accounts.view"
    ]
  },
  {
    id: "role-finance-manager",
    name: "Finance & Treasury Manager",
    description: "Full authority over ledger balances, payout initiation, threshold approvals, and fee configurations.",
    isSystem: true,
    memberCount: 1,
    permissions: [
      "balances.view", 
      "balances.fund", 
      "payouts.view", 
      "payouts.initiate", 
      "payouts.approve", 
      "payouts.beneficiaries", 
      "transactions.view", 
      "transactions.export", 
      "invoicing.manage"
    ]
  },
  {
    id: "role-support-analyst",
    name: "Support & Customer Analyst",
    description: "Read-only access to customer payment verification, transactions, and invoice receipts.",
    isSystem: true,
    memberCount: 1,
    permissions: [
      "transactions.view", 
      "invoicing.manage", 
      "virtual_accounts.view"
    ]
  },
  {
    id: "role-custom-auditor",
    name: "Operations & Compliance Auditor",
    description: "Custom role created to inspect payouts, transactions, and ledger balance statements without write access.",
    isSystem: false,
    memberCount: 1,
    permissions: [
      "transactions.view", 
      "transactions.export", 
      "payouts.view", 
      "balances.view", 
      "settings.view"
    ]
  }
];

// Initial Members
interface TeamMember {
  id: string;
  name: string;
  email: string;
  roleId: string;
  roleName: string;
  twoFactor: "Enabled" | "Disabled";
  status: "Active" | "Pending Invitation";
  joinedDate: string;
  isOwner: boolean;
}

const INITIAL_MEMBERS: TeamMember[] = [
  {
    id: "usr-1",
    name: "John Doe (Owner)",
    email: "john.doe@example.com",
    roleId: "role-super-admin",
    roleName: "Super Administrator",
    twoFactor: "Enabled",
    status: "Active",
    joinedDate: "Jan 12, 2024",
    isOwner: true
  },
  {
    id: "usr-2",
    name: "Jane Smith",
    email: "jane.smith@example.com",
    roleId: "role-developer",
    roleName: "Developer & Integration Lead",
    twoFactor: "Enabled",
    status: "Active",
    joinedDate: "Feb 03, 2024",
    isOwner: false
  },
  {
    id: "usr-3",
    name: "Chukwudi Okafor",
    email: "treasury@paytaara.com",
    roleId: "role-finance-manager",
    roleName: "Finance & Treasury Manager",
    twoFactor: "Enabled",
    status: "Active",
    joinedDate: "Mar 18, 2024",
    isOwner: false
  },
  {
    id: "usr-4",
    name: "Fatima Bello",
    email: "audit@paytaara.com",
    roleId: "role-custom-auditor",
    roleName: "Operations & Compliance Auditor",
    twoFactor: "Disabled",
    status: "Active",
    joinedDate: "Apr 22, 2024",
    isOwner: false
  },
  {
    id: "usr-5",
    name: "Support Desk Specialist",
    email: "help@paytaara.com",
    roleId: "role-support-analyst",
    roleName: "Support & Customer Analyst",
    twoFactor: "Disabled",
    status: "Pending Invitation",
    joinedDate: "Oct 01, 2024",
    isOwner: false
  }
];

export default function TeamManagementPage() {
  const router = useRouter();
  const [activeTab, setActiveTab] = useState("members");
  const [members, setMembers] = useState<TeamMember[]>(INITIAL_MEMBERS);
  const [roles, setRoles] = useState<Role[]>(INITIAL_ROLES);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedRoleFilter, setSelectedRoleFilter] = useState("all");

  // Create Custom Role Modal State
  const [isCreateRoleOpen, setIsCreateRoleOpen] = useState(false);
  const [newRoleName, setNewRoleName] = useState("");
  const [newRoleDesc, setNewRoleDesc] = useState("");
  const [selectedPermissions, setSelectedPermissions] = useState<string[]>([]);
  const [createRoleError, setCreateRoleError] = useState("");

  // View Permissions Modal State
  const [viewingRole, setViewingRole] = useState<Role | null>(null);

  // Invite Member Modal State
  const [isInviteOpen, setIsInviteOpen] = useState(false);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteName, setInviteName] = useState("");
  const [inviteRoleId, setInviteRoleId] = useState(roles[1]?.id || "");
  const [inviteSuccess, setInviteSuccess] = useState(false);

  // Filtered Members
  const filteredMembers = members.filter(m => {
    const matchesSearch = m.name.toLowerCase().includes(searchQuery.toLowerCase()) || 
                          m.email.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesRole = selectedRoleFilter === "all" || m.roleId === selectedRoleFilter;
    return matchesSearch && matchesRole;
  });

  // Toggle single permission for custom role
  const togglePermission = (permId: string) => {
    setSelectedPermissions(prev => 
      prev.includes(permId) ? prev.filter(p => p !== permId) : [...prev, permId]
    );
  };

  // Toggle all permissions in a category
  const toggleCategoryPermissions = (group: PermissionGroup) => {
    const groupPermIds = group.permissions.map(p => p.id);
    const allSelected = groupPermIds.every(id => selectedPermissions.includes(id));

    if (allSelected) {
      setSelectedPermissions(prev => prev.filter(id => !groupPermIds.includes(id)));
    } else {
      setSelectedPermissions(prev => Array.from(new Set([...prev, ...groupPermIds])));
    }
  };

  // Save new custom role
  const handleCreateRole = () => {
    if (!newRoleName.trim()) {
      setCreateRoleError("Please provide a name for the custom role.");
      return;
    }
    if (selectedPermissions.length === 0) {
      setCreateRoleError("Please select at least one permission for this role.");
      return;
    }

    const newRole: Role = {
      id: `role-custom-${Date.now()}`,
      name: newRoleName.trim(),
      description: newRoleDesc.trim() || "Custom organization role.",
      isSystem: false,
      memberCount: 0,
      permissions: selectedPermissions
    };

    setRoles([...roles, newRole]);
    setIsCreateRoleOpen(false);
    setNewRoleName("");
    setNewRoleDesc("");
    setSelectedPermissions([]);
    setCreateRoleError("");
  };

  // Delete Custom Role
  const handleDeleteRole = (roleId: string) => {
    const roleToDelete = roles.find(r => r.id === roleId);
    if (roleToDelete?.isSystem) return; // Cannot delete system roles

    // Prevent deletion if members are assigned
    const assignedCount = members.filter(m => m.roleId === roleId).length;
    if (assignedCount > 0) {
      alert(`Cannot delete this role because ${assignedCount} active member(s) are assigned to it. Please reassign them first.`);
      return;
    }

    setRoles(roles.filter(r => r.id !== roleId));
  };

  // Send invitation
  const handleSendInvite = (e: React.FormEvent) => {
    e.preventDefault();
    if (!inviteEmail) return;

    const assignedRole = roles.find(r => r.id === inviteRoleId);

    const newMember: TeamMember = {
      id: `usr-${Date.now()}`,
      name: inviteName.trim() || inviteEmail.split("@")[0],
      email: inviteEmail.trim(),
      roleId: inviteRoleId,
      roleName: assignedRole ? assignedRole.name : "Custom Role",
      twoFactor: "Disabled",
      status: "Pending Invitation",
      joinedDate: "Just now",
      isOwner: false
    };

    setMembers([...members, newMember]);
    setInviteSuccess(true);
    setTimeout(() => {
      setInviteSuccess(false);
      setIsInviteOpen(false);
      setInviteEmail("");
      setInviteName("");
    }, 1200);
  };

  // Remove member
  const handleRemoveMember = (id: string) => {
    const member = members.find(m => m.id === id);
    if (member?.isOwner) return;
    if (confirm(`Are you sure you want to revoke access for ${member?.name}?`)) {
      setMembers(members.filter(m => m.id !== id));
    }
  };

  return (
    <div className="space-y-6">
      
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 border-b pb-5">
        <div>
          <div className="flex items-center gap-2">
            <h1 className="text-2xl font-bold tracking-tight text-foreground">Users & Team Members</h1>
            <Badge variant="outline" className="bg-primary/10 text-primary border-primary/20 text-[10px] font-bold">
              Organization Governance
            </Badge>
          </div>
          <p className="text-xs text-muted-foreground mt-1">
            Manage your organization staff, define custom granular role permissions, and control dashboard privileges.
          </p>
        </div>

        <div className="flex items-center gap-2.5">
          <Button 
            onClick={() => setIsInviteOpen(true)}
            className="bg-primary text-primary-foreground font-bold text-xs h-9 px-4 shadow-sm"
          >
            <UserPlus className="mr-2 h-3.5 w-3.5" /> Invite Team Member
          </Button>
        </div>
      </div>

      {/* Top Metrics Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="border shadow-sm p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-[11px] font-semibold text-muted-foreground">Total Staff Users</p>
              <h3 className="text-2xl font-bold tracking-tight text-foreground mt-1">{members.length}</h3>
            </div>
            <div className="h-9 w-9 rounded-lg bg-primary/10 text-primary flex items-center justify-center font-bold">
              <Users className="h-4 w-4" />
            </div>
          </div>
        </Card>

        <Card className="border shadow-sm p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-[11px] font-semibold text-muted-foreground">Active Members</p>
              <h3 className="text-2xl font-bold tracking-tight text-emerald-600 mt-1">
                {members.filter(m => m.status === "Active").length}
              </h3>
            </div>
            <div className="h-9 w-9 rounded-lg bg-emerald-500/10 text-emerald-600 flex items-center justify-center font-bold">
              <CheckCircle2 className="h-4 w-4" />
            </div>
          </div>
        </Card>

        <Card className="border shadow-sm p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-[11px] font-semibold text-muted-foreground">Pending Invites</p>
              <h3 className="text-2xl font-bold tracking-tight text-amber-600 mt-1">
                {members.filter(m => m.status === "Pending Invitation").length}
              </h3>
            </div>
            <div className="h-9 w-9 rounded-lg bg-amber-500/10 text-amber-600 flex items-center justify-center font-bold">
              <Clock className="h-4 w-4" />
            </div>
          </div>
        </Card>

        <Card className="border shadow-sm p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-[11px] font-semibold text-muted-foreground">Roles Available</p>
              <h3 className="text-2xl font-bold tracking-tight text-foreground mt-1">
                {roles.length} <span className="text-xs font-normal text-muted-foreground">({roles.filter(r => !r.isSystem).length} Custom)</span>
              </h3>
            </div>
            <div className="h-9 w-9 rounded-lg bg-indigo-500/10 text-indigo-600 flex items-center justify-center font-bold">
              <ShieldCheck className="h-4 w-4" />
            </div>
          </div>
        </Card>
      </div>

      {/* Main Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full space-y-6">
        <TabsList className="h-11 w-full justify-start gap-2 bg-muted/40 p-1 rounded-xl border border-border">
          <TabsTrigger value="members" className="h-9 rounded-lg px-4 text-xs font-semibold data-[state=active]:bg-card data-[state=active]:text-primary data-[state=active]:shadow-sm">
            <Users className="h-3.5 w-3.5 mr-2" /> Team Members ({members.length})
          </TabsTrigger>
          <TabsTrigger value="roles" className="h-9 rounded-lg px-4 text-xs font-semibold data-[state=active]:bg-card data-[state=active]:text-primary data-[state=active]:shadow-sm">
            <Layers className="h-3.5 w-3.5 mr-2" /> Roles & Permissions ({roles.length})
          </TabsTrigger>
        </TabsList>

        {/* ============================================================== */}
        {/* TAB 1: TEAM MEMBERS LIST */}
        {/* ============================================================== */}
        <TabsContent value="members" className="space-y-4 m-0">
          <Card className="border shadow-sm overflow-hidden">
            <CardHeader className="py-4 px-5 border-b bg-muted/10 flex flex-col md:flex-row md:items-center justify-between gap-3">
              <div>
                <CardTitle className="text-sm font-bold flex items-center gap-2">
                  <Users className="h-4 w-4 text-primary" /> Active Staff Directory
                </CardTitle>
                <CardDescription className="text-[11px]">
                  All staff registered under your corporate organization with active or pending dashboard access.
                </CardDescription>
              </div>

              {/* Search & Filter Controls */}
              <div className="flex items-center gap-2.5">
                <div className="relative w-56">
                  <Search className="absolute left-2.5 top-2.5 h-3.5 w-3.5 text-muted-foreground" />
                  <Input 
                    placeholder="Search name or email..." 
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="h-8 pl-8 text-xs bg-card"
                  />
                </div>

                <select 
                  value={selectedRoleFilter}
                  onChange={(e) => setSelectedRoleFilter(e.target.value)}
                  className="h-8 rounded-md border border-input bg-card px-2 text-xs font-medium text-foreground focus:outline-none"
                >
                  <option value="all">All Roles</option>
                  {roles.map(r => (
                    <option key={r.id} value={r.id}>{r.name}</option>
                  ))}
                </select>
              </div>
            </CardHeader>

            <Table>
              <TableHeader className="bg-muted/30">
                <TableRow>
                  <TableHead className="h-10 text-xs font-bold">User Information</TableHead>
                  <TableHead className="h-10 text-xs font-bold">Assigned Role</TableHead>
                  <TableHead className="h-10 text-xs font-bold">2FA Security</TableHead>
                  <TableHead className="h-10 text-xs font-bold">Status</TableHead>
                  <TableHead className="h-10 text-xs font-bold">Joined Date</TableHead>
                  <TableHead className="h-10 text-xs font-bold text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredMembers.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center py-8 text-xs text-muted-foreground">
                      No team members match your search criteria.
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredMembers.map((member) => (
                    <TableRow key={member.id} className="hover:bg-muted/10 transition-colors">
                      <TableCell className="py-3">
                        <div className="flex items-center gap-3">
                          <div className="h-8 w-8 rounded-full bg-primary/10 text-primary border border-primary/20 flex items-center justify-center font-bold text-xs shrink-0">
                            {member.name.charAt(0)}
                          </div>
                          <div>
                            <div className="flex items-center gap-1.5">
                              <p className="text-xs font-bold text-foreground">{member.name}</p>
                              {member.isOwner && (
                                <Badge className="bg-primary/20 text-primary border-none text-[9px] font-bold py-0 h-4">Owner</Badge>
                              )}
                            </div>
                            <p className="text-[11px] text-muted-foreground">{member.email}</p>
                          </div>
                        </div>
                      </TableCell>
                      
                      <TableCell>
                        <Badge variant="outline" className="text-[10px] font-bold border-border bg-card">
                          {member.roleName}
                        </Badge>
                      </TableCell>

                      <TableCell>
                        <span className={cn(
                          "text-xs font-semibold flex items-center gap-1",
                          member.twoFactor === "Enabled" ? "text-emerald-600" : "text-amber-600"
                        )}>
                          <Lock className="h-3 w-3" /> {member.twoFactor}
                        </span>
                      </TableCell>

                      <TableCell>
                        <Badge className={cn(
                          "text-[9px] font-bold uppercase border-none",
                          member.status === "Active" 
                            ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-950/50 dark:text-emerald-400" 
                            : "bg-amber-100 text-amber-700 dark:bg-amber-950/50 dark:text-amber-400"
                        )}>
                          {member.status}
                        </Badge>
                      </TableCell>

                      <TableCell className="text-xs font-mono text-muted-foreground">
                        {member.joinedDate}
                      </TableCell>

                      <TableCell className="text-right">
                        {member.isOwner ? (
                          <span className="text-[11px] text-muted-foreground font-semibold px-2">Protected</span>
                        ) : (
                          <div className="flex items-center justify-end gap-1">
                            {member.status === "Pending Invitation" || member.status.toUpperCase() === "PENDING INVITATION" ? (
                              <Button 
                                variant="ghost" 
                                size="sm" 
                                className="h-7 text-xs font-semibold text-primary hover:text-primary hover:bg-primary/10 gap-1"
                                onClick={() => {
                                  const query = new URLSearchParams({
                                    name: member.name,
                                    email: member.email,
                                    role: member.roleName,
                                  }).toString();
                                  router.push(`/accept-invite?${query}`);
                                }}
                              >
                                Continue Registration
                                <ArrowRight className="h-3 w-3" />
                              </Button>
                            ) : (
                              <Button 
                                variant="ghost" 
                                size="sm" 
                                className="h-7 text-xs font-semibold text-muted-foreground hover:text-foreground"
                                onClick={() => {
                                  const nextRoleId = prompt(
                                    `Reassign role for ${member.name}:\n` + roles.map((r, i) => `${i + 1}. ${r.name}`).join("\n") + "\n\nEnter number:"
                                  );
                                  if (nextRoleId && roles[Number(nextRoleId) - 1]) {
                                    const selectedRole = roles[Number(nextRoleId) - 1];
                                    setMembers(members.map(m => m.id === member.id ? { ...m, roleId: selectedRole.id, roleName: selectedRole.name } : m));
                                  }
                                }}
                              >
                                Edit Role
                              </Button>
                            )}
                            <Button 
                              variant="ghost" 
                              size="icon" 
                              className="h-7 w-7 text-destructive hover:bg-destructive/10"
                              onClick={() => handleRemoveMember(member.id)}
                              title="Remove user"
                            >
                              <Trash2 className="h-3.5 w-3.5" />
                            </Button>
                          </div>
                        )}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </Card>
        </TabsContent>

        {/* ============================================================== */}
        {/* TAB 2: ROLES & PERMISSIONS SETUP */}
        {/* ============================================================== */}
        <TabsContent value="roles" className="space-y-6 m-0">
          
          {/* Top Banner & Create Role Button */}
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 p-4 rounded-xl border bg-muted/20">
            <div className="space-y-1">
              <h3 className="text-sm font-bold text-foreground flex items-center gap-2">
                <ShieldCheck className="h-4 w-4 text-primary" /> Role Access Control Matrix
              </h3>
              <p className="text-xs text-muted-foreground max-w-xl">
                System roles are built-in presets protected against tampering. Create custom roles with tailored titles and granular permissions for specialized staff.
              </p>
            </div>
            
            <Button 
              onClick={() => setIsCreateRoleOpen(true)}
              className="bg-primary text-primary-foreground font-bold text-xs h-9 px-4 shrink-0 shadow-sm"
            >
              <Plus className="mr-2 h-3.5 w-3.5" /> Create Custom Role
            </Button>
          </div>

          {/* Section 1: System Defined Roles (Immutable) */}
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <Lock className="h-3.5 w-3.5 text-muted-foreground" />
              <h4 className="text-xs font-bold uppercase tracking-wider text-muted-foreground">
                System Roles (Read-Only / Protected)
              </h4>
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              {roles.filter(r => r.isSystem).map((role) => (
                <Card key={role.id} className="border shadow-sm flex flex-col justify-between hover:border-primary/30 transition-all">
                  <CardHeader className="py-3.5 px-4 border-b bg-muted/10 flex flex-row items-start justify-between gap-2">
                    <div>
                      <div className="flex items-center gap-2">
                        <CardTitle className="text-xs font-bold">{role.name}</CardTitle>
                        <Badge variant="outline" className="text-[9px] font-bold bg-muted text-muted-foreground border-border flex items-center gap-1">
                          <Lock className="w-2.5 h-2.5" /> System Default
                        </Badge>
                      </div>
                      <CardDescription className="text-[11px] mt-1 leading-relaxed">
                        {role.description}
                      </CardDescription>
                    </div>
                  </CardHeader>

                  <CardContent className="p-4 flex items-center justify-between gap-3 text-xs">
                    <div className="flex items-center gap-4 text-muted-foreground text-[11px]">
                      <span><strong>{role.permissions.length}</strong> permissions active</span>
                      <span><strong>{members.filter(m => m.roleId === role.id).length}</strong> staff assigned</span>
                    </div>

                    <Button 
                      variant="outline" 
                      size="sm" 
                      onClick={() => setViewingRole(role)}
                      className="h-7 text-xs font-semibold px-2.5"
                    >
                      <Eye className="w-3 h-3 mr-1.5" /> View Matrix
                    </Button>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>

          {/* Section 2: Custom Roles */}
          <div className="space-y-3 pt-4 border-t">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Sparkles className="h-3.5 w-3.5 text-primary" />
                <h4 className="text-xs font-bold uppercase tracking-wider text-muted-foreground">
                  Custom Defined Roles ({roles.filter(r => !r.isSystem).length})
                </h4>
              </div>
            </div>

            {roles.filter(r => !r.isSystem).length === 0 ? (
              <div className="p-8 text-center border rounded-xl bg-card border-dashed space-y-2">
                <ShieldAlert className="h-8 w-8 text-muted-foreground/60 mx-auto" />
                <h5 className="text-xs font-bold text-foreground">No Custom Roles Configured</h5>
                <p className="text-[11px] text-muted-foreground max-w-sm mx-auto">
                  Create custom roles to assign specific rights like Payout Approvals, Developer API inspection, or Invoicing without giving full administrative privileges.
                </p>
                <Button 
                  size="sm" 
                  onClick={() => setIsCreateRoleOpen(true)}
                  className="mt-2 h-8 text-xs font-bold"
                >
                  <Plus className="mr-1.5 h-3.5 w-3.5" /> Add Custom Role
                </Button>
              </div>
            ) : (
              <div className="grid md:grid-cols-2 gap-4">
                {roles.filter(r => !r.isSystem).map((role) => (
                  <Card key={role.id} className="border shadow-sm flex flex-col justify-between border-primary/20 bg-card hover:shadow-md transition-all">
                    <CardHeader className="py-3.5 px-4 border-b bg-primary/5 flex flex-row items-start justify-between gap-2">
                      <div>
                        <div className="flex items-center gap-2">
                          <CardTitle className="text-xs font-bold text-foreground">{role.name}</CardTitle>
                          <Badge className="text-[9px] font-bold bg-primary text-primary-foreground border-none">
                            Custom Role
                          </Badge>
                        </div>
                        <CardDescription className="text-[11px] mt-1 leading-relaxed">
                          {role.description}
                        </CardDescription>
                      </div>
                    </CardHeader>

                    <CardContent className="p-4 flex items-center justify-between gap-3 text-xs">
                      <div className="flex items-center gap-4 text-muted-foreground text-[11px]">
                        <span><strong>{role.permissions.length}</strong> permissions active</span>
                        <span><strong>{members.filter(m => m.roleId === role.id).length}</strong> staff assigned</span>
                      </div>

                      <div className="flex items-center gap-1.5">
                        <Button 
                          variant="outline" 
                          size="sm" 
                          onClick={() => setViewingRole(role)}
                          className="h-7 text-xs font-semibold px-2.5"
                        >
                          <Eye className="w-3 h-3 mr-1" /> View Matrix
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="icon" 
                          className="h-7 w-7 text-destructive hover:bg-destructive/10"
                          onClick={() => handleDeleteRole(role.id)}
                          title="Delete Custom Role"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </div>
        </TabsContent>
      </Tabs>

      {/* ============================================================== */}
      {/* MODAL 1: CREATE CUSTOM ROLE WITH CATEGORIZED CHECKBOXES */}
      {/* ============================================================== */}
      <Dialog open={isCreateRoleOpen} onOpenChange={setIsCreateRoleOpen}>
        <DialogContent className="sm:max-w-[700px] max-h-[90vh] flex flex-col p-0">
          <DialogHeader className="p-5 pb-3 border-b">
            <div className="flex items-center gap-2">
              <div className="h-8 w-8 rounded-lg bg-primary/10 text-primary flex items-center justify-center font-bold">
                <ShieldCheck className="h-4 w-4" />
              </div>
              <div>
                <DialogTitle className="text-base font-bold">Create Custom Organization Role</DialogTitle>
                <DialogDescription className="text-xs">
                  Configure a custom title and select permission checkboxes grouped by operational category.
                </DialogDescription>
              </div>
            </div>
          </DialogHeader>

          <div className="p-5 space-y-5 overflow-y-auto flex-1">
            {createRoleError && (
              <div className="p-3 rounded-lg bg-destructive/10 text-destructive text-xs font-semibold flex items-center gap-2 border border-destructive/20">
                <ShieldAlert className="h-4 w-4 shrink-0" />
                {createRoleError}
              </div>
            )}

            {/* Basic Info */}
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label htmlFor="role-name" className="text-xs font-bold">Role Title <span className="text-destructive">*</span></Label>
                <Input 
                  id="role-name" 
                  placeholder="e.g. Disbursement Approver" 
                  value={newRoleName}
                  onChange={(e) => setNewRoleName(e.target.value)}
                  className="h-9 text-xs"
                />
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="role-desc" className="text-xs font-bold">Description</Label>
                <Input 
                  id="role-desc" 
                  placeholder="e.g. Can initiate transfers and inspect statements" 
                  value={newRoleDesc}
                  onChange={(e) => setNewRoleDesc(e.target.value)}
                  className="h-9 text-xs"
                />
              </div>
            </div>

            {/* Permission Checkboxes Grouped by Category */}
            <div className="space-y-4 pt-2">
              <div className="flex items-center justify-between border-b pb-2">
                <Label className="text-xs font-bold uppercase tracking-wider text-muted-foreground">
                  Privileges Matrix ({selectedPermissions.length} selected)
                </Label>
                <div className="flex items-center gap-2">
                  <Button 
                    type="button" 
                    variant="ghost" 
                    size="sm" 
                    className="h-6 text-[10px] font-bold text-primary"
                    onClick={() => setSelectedPermissions(ALL_PERMISSION_GROUPS.flatMap(g => g.permissions.map(p => p.id)))}
                  >
                    Select All
                  </Button>
                  <Button 
                    type="button" 
                    variant="ghost" 
                    size="sm" 
                    className="h-6 text-[10px] font-bold text-muted-foreground"
                    onClick={() => setSelectedPermissions([])}
                  >
                    Clear All
                  </Button>
                </div>
              </div>

              <div className="space-y-4">
                {ALL_PERMISSION_GROUPS.map((group) => {
                  const groupPermIds = group.permissions.map(p => p.id);
                  const isAllChecked = groupPermIds.every(id => selectedPermissions.includes(id));
                  const isSomeChecked = groupPermIds.some(id => selectedPermissions.includes(id));

                  return (
                    <div key={group.category} className="rounded-xl border bg-card p-3.5 space-y-3">
                      {/* Category Header */}
                      <div className="flex items-center justify-between border-b pb-2">
                        <div className="flex items-center gap-2">
                          <group.icon className="h-4 w-4 text-primary" />
                          <span className="text-xs font-bold text-foreground">{group.category}</span>
                          <span className="text-[10px] text-muted-foreground font-mono">({group.permissions.length})</span>
                        </div>
                        <Button 
                          type="button" 
                          variant="ghost" 
                          size="sm" 
                          className="h-6 text-[10px] font-semibold text-muted-foreground hover:text-foreground"
                          onClick={() => toggleCategoryPermissions(group)}
                        >
                          {isAllChecked ? "Deselect Group" : "Select Group"}
                        </Button>
                      </div>

                      {/* Checkboxes Grid */}
                      <div className="grid sm:grid-cols-2 gap-2.5 pt-1">
                        {group.permissions.map((perm) => {
                          const isChecked = selectedPermissions.includes(perm.id);
                          return (
                            <div 
                              key={perm.id} 
                              onClick={() => togglePermission(perm.id)}
                              className={cn(
                                "flex items-start gap-2.5 p-2 rounded-lg border transition-all cursor-pointer select-none",
                                isChecked ? "bg-primary/5 border-primary/40 shadow-xs" : "bg-muted/10 border-border hover:bg-muted/30"
                              )}
                            >
                              <Checkbox 
                                checked={isChecked} 
                                onCheckedChange={() => togglePermission(perm.id)}
                                className="mt-0.5"
                              />
                              <div className="space-y-0.5">
                                <p className="text-xs font-bold text-foreground leading-none">{perm.name}</p>
                                <p className="text-[10px] text-muted-foreground leading-tight">{perm.description}</p>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          <DialogFooter className="p-4 border-t bg-muted/10 flex justify-between sm:justify-between">
            <span className="text-xs font-semibold text-muted-foreground flex items-center">
              Total Permissions: <strong className="ml-1 text-foreground">{selectedPermissions.length}</strong>
            </span>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={() => setIsCreateRoleOpen(false)} className="text-xs">
                Cancel
              </Button>
              <Button size="sm" onClick={handleCreateRole} className="text-xs font-bold bg-primary text-primary-foreground">
                <Check className="mr-1.5 h-3.5 w-3.5" /> Save Custom Role
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ============================================================== */}
      {/* MODAL 2: VIEW PERMISSIONS MATRIX MODAL */}
      {/* ============================================================== */}
      <Dialog open={!!viewingRole} onOpenChange={() => setViewingRole(null)}>
        <DialogContent className="sm:max-w-[600px] max-h-[85vh] flex flex-col p-0">
          <DialogHeader className="p-5 pb-3 border-b">
            <div className="flex items-center justify-between">
              <div>
                <div className="flex items-center gap-2">
                  <DialogTitle className="text-base font-bold">{viewingRole?.name}</DialogTitle>
                  {viewingRole?.isSystem ? (
                    <Badge variant="outline" className="text-[9px] font-bold bg-muted text-muted-foreground border-border">
                      <Lock className="w-2.5 h-2.5 mr-1" /> System Protected
                    </Badge>
                  ) : (
                    <Badge className="text-[9px] font-bold bg-primary text-primary-foreground border-none">
                      Custom Role
                    </Badge>
                  )}
                </div>
                <DialogDescription className="text-xs mt-1">
                  {viewingRole?.description}
                </DialogDescription>
              </div>
            </div>
          </DialogHeader>

          <div className="p-5 space-y-4 overflow-y-auto flex-1">
            {ALL_PERMISSION_GROUPS.map((group) => {
              const activeInGroup = group.permissions.filter(p => viewingRole?.permissions.includes(p.id));
              if (activeInGroup.length === 0) return null;

              return (
                <div key={group.category} className="space-y-2 border-b pb-3">
                  <div className="flex items-center gap-2 text-xs font-bold text-foreground">
                    <group.icon className="h-3.5 w-3.5 text-primary" />
                    {group.category}
                    <Badge variant="outline" className="text-[9px] font-mono ml-auto">
                      {activeInGroup.length} / {group.permissions.length}
                    </Badge>
                  </div>

                  <div className="grid sm:grid-cols-2 gap-2 pt-1">
                    {group.permissions.map((p) => {
                      const hasPerm = viewingRole?.permissions.includes(p.id);
                      return (
                        <div 
                          key={p.id}
                          className={cn(
                            "flex items-center gap-2 p-2 rounded-lg text-xs",
                            hasPerm ? "bg-emerald-500/10 text-foreground border border-emerald-500/20" : "opacity-40 text-muted-foreground"
                          )}
                        >
                          {hasPerm ? (
                            <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600 shrink-0" />
                          ) : (
                            <div className="h-3.5 w-3.5 rounded-full border border-muted-foreground/30 shrink-0" />
                          )}
                          <span className="font-medium text-[11px] truncate">{p.name}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>

          <DialogFooter className="p-4 border-t bg-muted/10">
            <Button size="sm" onClick={() => setViewingRole(null)} className="w-full text-xs font-bold">
              Close Matrix
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ============================================================== */}
      {/* MODAL 3: INVITE TEAM MEMBER */}
      {/* ============================================================== */}
      <Dialog open={isInviteOpen} onOpenChange={setIsInviteOpen}>
        <DialogContent className="sm:max-w-[450px]">
          <DialogHeader>
            <DialogTitle className="text-base font-bold">Invite New Staff Member</DialogTitle>
            <DialogDescription className="text-xs">
              Send an email onboarding link to grant access to your corporate merchant console.
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleSendInvite} className="space-y-4 py-2">
            <div className="space-y-1.5">
              <Label htmlFor="inv-email" className="text-xs font-bold">Corporate Email Address</Label>
              <Input 
                id="inv-email" 
                type="email" 
                required 
                placeholder="colleague@yourcompany.com" 
                value={inviteEmail}
                onChange={(e) => setInviteEmail(e.target.value)}
                className="h-9 text-xs" 
              />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="inv-name" className="text-xs font-bold">Full Name (Optional)</Label>
              <Input 
                id="inv-name" 
                placeholder="e.g. Alex Morgan" 
                value={inviteName}
                onChange={(e) => setInviteName(e.target.value)}
                className="h-9 text-xs" 
              />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="inv-role" className="text-xs font-bold">Assign Role</Label>
              <select 
                id="inv-role" 
                value={inviteRoleId}
                onChange={(e) => setInviteRoleId(e.target.value)}
                className="flex h-9 w-full rounded-md border border-input bg-card px-3 py-1 text-xs font-medium text-foreground focus:outline-none"
              >
                <optgroup label="System Roles">
                  {roles.filter(r => r.isSystem).map(r => (
                    <option key={r.id} value={r.id}>{r.name}</option>
                  ))}
                </optgroup>
                {roles.filter(r => !r.isSystem).length > 0 && (
                  <optgroup label="Custom Roles">
                    {roles.filter(r => !r.isSystem).map(r => (
                      <option key={r.id} value={r.id}>{r.name}</option>
                    ))}
                  </optgroup>
                )}
              </select>
            </div>

            <div className="p-3 rounded-lg bg-muted/30 border text-[11px] text-muted-foreground flex items-center gap-2">
              <Info className="h-4 w-4 shrink-0 text-primary" />
              <span>Invited staff will receive a secured magic token to set their password and activate 2FA.</span>
            </div>

            <DialogFooter className="pt-2">
              <Button type="button" variant="outline" size="sm" onClick={() => setIsInviteOpen(false)} className="text-xs">
                Cancel
              </Button>
              <Button type="submit" size="sm" className="text-xs font-bold bg-primary text-primary-foreground">
                {inviteSuccess ? (
                  <>
                    <Check className="mr-1.5 h-3.5 w-3.5 text-emerald-400" /> Invitation Sent!
                  </>
                ) : (
                  <>
                    <Mail className="mr-1.5 h-3.5 w-3.5" /> Send Invitation
                  </>
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

    </div>
  );
}
